import { DATASETS } from '@/components/Constants';
import { patchItems } from '@/components/Patches/PatchSearchItems';
import type { NavigateSearchOptions } from '@/hooks/search/useSearchTabNavigation';

export const SPOTLIGHT_Z_INDEX = 10050;
export const SPOTLIGHT_MIN_QUERY_LENGTH = 3;
export const SPOTLIGHT_DEBOUNCE_MS = 300;

type TabActionResult = { ok: true } | { ok: false; reason: 'max_tabs' | 'not_found' };

export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform) || /Mac OS/.test(navigator.userAgent);
}

export function getSpotlightShortcutLabel(): string {
  return isMacPlatform() ? '⌘K' : 'Ctrl+K';
}

export function isSpotlightHotkey(event: KeyboardEvent): boolean {
  if (event.altKey || event.shiftKey) return false;
  if (event.key.toLowerCase() !== 'k') return false;
  return event.metaKey || event.ctrlKey;
}

export function isTypingInForeignDialog(event: KeyboardEvent): boolean {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return false;

  const tag = target.tagName;
  const isField = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
  if (!isField) return false;

  const dialog = target.closest('[role="dialog"]');
  if (!dialog) return false;
  return dialog.getAttribute('data-search-spotlight') !== 'true';
}

export type SpotlightSearchItem = { dataset?: string; id?: string; [key: string]: any };

export function toSpotlightItemList(
  items: SpotlightSearchItem | SpotlightSearchItem[],
): SpotlightSearchItem[] {
  return Array.isArray(items) ? items : [items];
}

export function buildSpotlightSearchQuery(
  items: SpotlightSearchItem | SpotlightSearchItem[],
  options: { restrictSources?: string; groups?: string[] | null },
): string {
  const list = toSpotlightItemList(items);
  const first = list[0];
  const view = first?.dataset || DATASETS.DISEASES;
  const tab = view === DATASETS.VARIANTS ? 'VDA' : 'GDA';
  const isAcademic = Boolean(options.groups?.includes('ROLE_ACADEMIC'));
  const source = options.restrictSources === 'CURATED' || isAcademic ? 'CURATED' : 'ALL';
  const idents = list
    .map(item => item.id)
    .filter(Boolean)
    .join('-');
  return `?view=${view}&idents=${idents}&source=${source}&tab=${tab}`;
}

export function toggleSpotlightSelection(selected: SpotlightSearchItem[], item: any): SpotlightSearchItem[] {
  const patched = patchItems(item) as SpotlightSearchItem;
  const selectedDataset = selected[0]?.dataset;
  if (selectedDataset && patched.dataset && selectedDataset !== patched.dataset) {
    return selected;
  }
  if (selected.some(prev => prev.id === patched.id)) {
    return selected.filter(prev => prev.id !== patched.id);
  }
  return [...selected, patched];
}

export function openSpotlightSearch(
  navigateSearch: (options: NavigateSearchOptions) => TabActionResult,
  payload: { search: string; title: string; searchItems: unknown[] },
): void {
  const result = navigateSearch({ ...payload, openInNewTab: true });
  if (!result.ok) {
    navigateSearch({ ...payload, openInNewTab: false });
  }
}
